import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackFeature, trackPageView, trackSection, trackSectionTime, trackSessionEnd } from "@/lib/analytics";

function clickName(element) {
  if (element.dataset.analytics) return element.dataset.analytics;
  if (element.dataset.testid) return element.dataset.testid;
  const label = element.getAttribute("aria-label");
  if (label) return `control:${label}`;
  if (element.tagName === "A") {
    try {
      const url = new URL(element.href, window.location.origin);
      return url.origin === window.location.origin ? `link:${url.pathname}` : `external:${url.hostname}`;
    } catch { return null; }
  }
  return null;
}

export default function AnalyticsTracker() {
  const location = useLocation();
  const startedAt = useRef(Date.now());
  const lastPoint = useRef(location.pathname || "/");
  const maxScroll = useRef(0);

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    startedAt.current = Date.now();
    lastPoint.current = location.pathname || "/";
    maxScroll.current = 0;
    trackPageView(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (event) => {
      if (location.pathname.startsWith("/admin")) return;
      const target = event.target instanceof Element ? event.target.closest("a,button,[role='button']") : null;
      if (!target) return;
      const name = clickName(target);
      if (name) trackFeature(name, location.pathname, { target: target.tagName.toLowerCase() });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== "/") return;
    const seen = new Set();
    const state = new Map();
    const stopTiming = (item, now = performance.now(), immediate = false) => {
      if (item.startedAt == null) return;
      trackSectionTime(item.name, "/", now - item.startedAt, immediate);
      item.startedAt = null;
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const name = entry.target.getAttribute("data-analytics-section") || entry.target.id;
        if (!name) return;
        const item = state.get(entry.target) || { name, visible: false, startedAt: null };
        item.visible = entry.isIntersecting && entry.intersectionRatio >= 0.35;
        state.set(entry.target, item);
        if (item.visible) {
          lastPoint.current = `section:${name}`;
          if (document.visibilityState === "visible" && item.startedAt == null) item.startedAt = performance.now();
          if (!seen.has(name)) {
            seen.add(name);
            trackSection(name, "/");
          }
        } else {
          stopTiming(item);
        }
      });
    }, { threshold: [0, 0.35, 0.6] });
    const observed = new Set();
    const discoverSections = () => {
      const sections = Array.from(document.querySelectorAll("main section[id], main [data-analytics-section]"));
      sections.forEach((section) => {
        if (observed.has(section)) return;
        observed.add(section);
        observer.observe(section);
      });
      if (sections.length) mutationObserver.disconnect();
    };
    const mutationObserver = new MutationObserver(discoverSections);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    discoverSections();
    const onVisibility = () => {
      const now = performance.now();
      state.forEach((item) => {
        if (document.visibilityState === "visible" && item.visible) item.startedAt = now;
        else stopTiming(item, now, true);
      });
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      state.forEach(item => stopTiming(item, performance.now(), true));
      document.removeEventListener("visibilitychange", onVisibility);
      mutationObserver.disconnect();
      observer.disconnect();
    };
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => {
      const available = document.documentElement.scrollHeight - window.innerHeight;
      if (available > 0) maxScroll.current = Math.max(maxScroll.current, (window.scrollY / available) * 100);
    };
    const onPageHide = () => {
      if (location.pathname.startsWith("/admin")) return;
      trackSessionEnd(lastPoint.current, location.pathname, Date.now() - startedAt.current, maxScroll.current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [location.pathname]);

  return null;
}
